import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { shouldForwardProp } from 'commons/utils/mui'
import { theme } from 'styles/theme/hav-theme'

import type { ButtonVariants } from './SlimButton'

type StyledButtonProps = {
  variant: ButtonVariants
  /** @deprecated Use `size` prop instead height */
  height?: number
  size: keyof typeof theme.sizes.buttons
  loading?: boolean
  iconColor?: string // TODO: Replace with `colors` keys
}

type StyledButtonTextProps = {
  size: keyof typeof theme.sizes.buttons
}

const variantModifiers = {
  active: css`
    background: ${theme.colors.ion.state.focus.soft};
    color: ${theme.colors.ion.state.focus.onsoft};

    svg {
      path {
        fill: ${theme.colors.ion.state.focus.onsoft};
      }
    }

    :disabled {
      background-color: ${theme.colors.neutral.clear};
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  border: css`
    background: transparent;
    color: ${theme.colors.ion.primary.main};
    border: 1px solid ${theme.colors.ion.state.enabled.borderneutral};

    svg {
      path {
        fill: ${theme.colors.ion.primary.main};
      }
    }

    :hover {
      background-color: ${theme.colors.neutral.clear};
    }

    :disabled {
      background-color: transparent;
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  danger: css`
    background: ${theme.colors.alert.danger.dark};
    color: ${theme.colors.neutral.white};

    svg {
      path {
        fill: ${theme.colors.neutral.white};
      }
    }

    :hover {
      background-color: ${theme.colors.alert.danger.darker};
    }

    :disabled {
      background-color: ${theme.colors.neutral.clear};
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  ghost: css`
    background: transparent;
    color: ${theme.colors.neutral.high};

    svg {
      path {
        fill: ${theme.colors.primary.main};
      }
    }

    :hover {
      background-color: ${theme.colors.neutral.clear};
    }

    :disabled {
      background-color: transparent;
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  icon: css`
    background: transparent;

    svg {
      path {
        fill: ${theme.colors.primary.main};
      }
    }

    :hover {
      filter: brightness(90%);
      transition: filter 0.2s ease;
    }

    :disabled {
      background-color: transparent;
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  info: css`
    background: ${theme.colors.ion.primary.softer};
    color: ${theme.colors.ion.primary.main};

    svg {
      path {
        fill: ${theme.colors.ion.primary.main};
      }
    }

    :hover {
      background: ${theme.colors.ion.primary.onsofter};
      color: ${theme.colors.neutral.white};

      svg {
        path {
          fill: ${theme.colors.neutral.white};
        }
      }
    }

    :disabled {
      background-color: ${theme.colors.neutral.clear};
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  primary: css`
    background: ${theme.colors.primary.main};
    color: ${theme.colors.neutral.white};

    svg {
      path {
        fill: ${theme.colors.neutral.white};
      }
    }

    :hover {
      background-color: ${theme.colors.primary.hover};
    }

    :disabled {
      background-color: ${theme.colors.neutral.clear};
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  secondary: css`
    background: transparent;
    color: ${theme.colors.neutral.high};

    svg {
      path {
        fill: ${theme.colors.primary.main};
      }
    }

    :hover {
      background-color: ${theme.colors.neutral.clear};
    }

    :disabled {
      background-color: transparent;
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  success: css`
    background: ${theme.colors.alert.success.dark};
    color: ${theme.colors.neutral.white};

    svg {
      path {
        fill: ${theme.colors.neutral.white};
      }
    }

    :hover {
      background-color: ${theme.colors.alert.success.darker};
    }

    :disabled {
      background-color: ${theme.colors.neutral.clear};
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  transparent: css`
    background: transparent;
    color: ${theme.colors.neutral.black};
    border: 1px solid ${theme.colors.neutral.black};

    svg {
      path {
        fill: ${theme.colors.neutral.black};
      }
    }

    :hover {
      background-color: ${theme.colors.neutral.clear};
    }

    :disabled {
      background-color: transparent;
      color: ${theme.colors.neutral.mediumhover};
      border: 1px solid ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  warning: css`
    background: ${theme.colors.alert.warning.dark};
    color: ${theme.colors.neutral.white};

    svg {
      path {
        fill: ${theme.colors.neutral.white};
      }
    }

    :hover {
      background-color: ${theme.colors.alert.warning.darker};
    }

    :disabled {
      background-color: ${theme.colors.neutral.clear};
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
  white: css`
    background: ${theme.colors.neutral.white};
    color: ${theme.colors.primary.main};

    svg {
      path {
        fill: ${theme.colors.primary.main};
      }
    }

    :hover {
      background-color: ${theme.colors.neutral.clear};
    }

    :disabled {
      background-color: transparent;
      color: ${theme.colors.neutral.mediumhover};

      svg {
        path {
          fill: ${theme.colors.neutral.mediumhover};
        }
      }
    }
  `,
}

const waveAnim = keyframes`
  0% {
    background-position: -500px 0;
  }
  100% {
    background-position: 500px 0;
  }
`

export const StyledButton = styled('button', {
  shouldForwardProp,
})<StyledButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: ${theme.shapes.borderRadius.sm};
  padding: ${theme.spacing.sm};
  height: ${({ size, height }) => height || theme.sizes.buttons[size || 'md']};

  transition: background 0.2s ease-in-out;

  :disabled {
    cursor: default;
    pointer-events: none;
  }

  ${({ variant }) => variantModifiers[variant]}

  ${({ loading, variant }) =>
    loading &&
    css`
      opacity: 0.8;
      pointer-events: none;
      ${
        variant === 'primary' &&
        css`
        background: linear-gradient(
          to right,
          ${theme.colors.primary.hover} 0%,
          ${theme.colors.primary.main} 50%,
          ${theme.colors.primary.hover} 100%
        );
      `
      }
      ${
        variant !== 'primary' &&
        css`
        background: linear-gradient(
          to right,
          ${theme.colors.neutral.clear} 0%,
          ${theme.colors.neutral.lighthover} 50%,
          ${theme.colors.neutral.clear} 100%
        );
      `
      }
      background-size: 500px 100%;
      animation: ${waveAnim} 2s ease-in-out infinite;
    `}

    /* NOTE: Override default icon variantModifiers color  */
    ${({ iconColor }) =>
      iconColor &&
      css`
      :hover {
        filter: brightness(90%);
        transition: filter 0.2s ease;
      }
      svg {
        path {
          fill: ${iconColor};
        }
      }
    `}
`

export const ButtonText = styled.span<StyledButtonTextProps>`
  font-family: ${theme.typography.family.bold};
  font-size: ${({ size }) =>
    ['lg', 'md', 'sm'].includes(size) ? theme.typography.sizes.xs : theme.typography.sizes['2xs']};
  line-height: ${theme.typography.sizes.xs};
  color: inherit;
`
